import {Trans} from '@lingui/macro';
import React, {FC, useEffect, useRef, useState} from 'react';

import {AsyncHandlerOf, Handler} from '../../../../../libs/shared/helpers/src/react/reactHelpers';
import {usePrevious} from '../../../../../srcCommon/helpers/hookHelpers';
import SalesforceImgSrc from '../../../../assets/images/integrations/salesforce16.svg';
import {CrmSalesforceFieldModel} from '../../../../models/api/crmSalesforceFieldModel';
import {CrmSalesforceMetadataModel} from '../../../../models/api/crmSalesforceMetadataModel';
import {
  CrmSyncErrorModel,
  CrmSyncIntegrationResourceTypesEnum,
  CrmSyncModel,
  CrmSyncSourcesEnum,
  CrmSyncStatusesEnum,
} from '../../../../models/api/crmSyncModel';
import {CustomFieldModel} from '../../../../models/api/customFieldModel';
import {SyncJobModel} from '../../../../models/api/jobModel';
import {CrmPreviewParentPanelId} from '../../../../store/panels/contacts/contactsManager/crmPreview/crmPreviewActions';
import {CrmLastSyncedBannerContainer} from '../../../common/crm/crmLastSyncedBannerContainer';
import {CrmSyncErrorsBannerContainer} from '../banners/crmSyncErrorsBannerContainer';
import {CrmSyncingBanner} from '../banners/crmSyncingBanner';
import {computeCrmSyncMapping} from '../common/crmSyncHelpers';
import {CrmSyncInfoBox} from '../common/crmSyncInfoBox';
import {CrmSyncStepOneContainer} from '../common/crmSyncStepOneContainer';
import {CrmSyncErrorsPageContainer} from '../common/errors/crmSyncErrorsPageContainer';
import {useMappedFields} from '../common/hooks/useMappedFields';
import {useUpdateCrmSync} from '../common/hooks/useUpdateCrmSync';
import {CrmSyncDialogContainer} from '../crmSyncDialogContainer';
import {SalesforceCrmSyncStepThree} from './salesforceCrmSyncStepThree';
import {SalesforceCrmSyncStepTwo} from './salesforceCrmSyncStepTwo';
import {
  isMaxSalesforceAllAccountsReached,
  SalesforceAccountType,
  SalesforceAccountTypesEnum,
  SalesforceOptionalDefaultFields,
} from './salesforceHelpers';

/*
 * Props.
 */

export interface SalesforceCrmSyncDialogProps {
  panelId: CrmPreviewParentPanelId;
  isConnected: boolean;
  isAdmin: boolean;
  hasExpiredCredentials: boolean;
  customFields: ReadonlyArray<CustomFieldModel>;
  isLoading?: boolean;
  crmSync?: CrmSyncModel;
  username?: string;
  salesforceFields?: ReadonlyArray<CrmSalesforceFieldModel>;
  salesforceMetadata?: CrmSalesforceMetadataModel;
  activeSyncJob?: SyncJobModel;
  shouldStartOnErrorsPage?: boolean;
  disableCrmSyncAsync: AsyncHandlerOf<number>;
  onRequestClose: Handler;
  hasCrmSyncFeature: boolean;
}

/*
 * Component.
 */

export const SalesforceCrmSyncDialog: FC<SalesforceCrmSyncDialogProps> = (props) => {
  const {
    panelId,
    isLoading,
    crmSync,
    username,
    isConnected,
    salesforceFields,
    salesforceMetadata,
    isAdmin,
    activeSyncJob,
    hasExpiredCredentials,
    customFields,
    shouldStartOnErrorsPage,
    onRequestClose,
    disableCrmSyncAsync,
    hasCrmSyncFeature,
  } = props;

  const isConnectedAndValid = isConnected && !hasExpiredCredentials;
  const previousCrmSync = usePrevious(crmSync);
  const previousIsConnectedAndValid = usePrevious(isConnectedAndValid);
  const contentWrapperRef = useRef<HTMLDivElement>(null);
  const [isShowingErrorsPage, setIsShowingErrorsPage] = useState(Boolean(shouldStartOnErrorsPage));

  const {
    mappedFields,
    hasInvalidMappedField,
    onResetMappedFields,
    onRemoveMappedField,
    onAddMappedField,
    onUpdateMappedField,
  } = useMappedFields(contentWrapperRef, customFields, crmSync, salesforceFields);

  // This controls if set 2 and 3 are in a loading state.
  const isSectionsLoading = isConnectedAndValid && (isLoading || !salesforceFields || !salesforceMetadata);

  // Since this is not a panel, all state will be store in the component since it is base for all steps.
  const [selectedAccountType, setSelectedAccountType] = useState<SalesforceAccountType | undefined>(
    computeSelectedAccountType(crmSync),
  );
  const [hasValidList, setHasValidList] = useState(Boolean(computeSelectedAccountType(crmSync)));
  const {isUpdatingCrmSync, onUpdateCrmSyncAsync} = useUpdateCrmSync(
    crmSync,
    mappedFields,
    onRequestClose,
    selectedAccountType?.listId,
  );

  useEffect(() => {
    // If the credentials have expired, we should just show the default fields.
    if (hasExpiredCredentials || !isConnectedAndValid) {
      onResetMappedFields(SalesforceOptionalDefaultFields);
      return;
    }

    const wasSyncJustLoaded = previousCrmSync === undefined && crmSync;
    const wasIntegrationJustConnected = previousIsConnectedAndValid === false && isConnectedAndValid;
    if (wasSyncJustLoaded || wasIntegrationJustConnected) {
      onResetMappedFields(computeCrmSyncMapping(crmSync));
      setSelectedAccountType(computeSelectedAccountType(crmSync));
    }
  }, [
    crmSync,
    previousCrmSync,
    hasExpiredCredentials,
    previousIsConnectedAndValid,
    isConnectedAndValid,
    onResetMappedFields,
  ]);

  const onSelectAccountType = (accountType: SalesforceAccountType) => {
    setSelectedAccountType(accountType);

    // If we do not have a list id selected yet or if we have a "max accounts" error, we should not scroll away.
    if (
      (accountType.type === SalesforceAccountTypesEnum.LIST && !accountType.listId) ||
      (accountType.type === SalesforceAccountTypesEnum.ALL &&
        isMaxSalesforceAllAccountsReached(salesforceMetadata))
    ) {
      return;
    }
    if (contentWrapperRef.current) {
      contentWrapperRef.current.scrollTo({top: contentWrapperRef.current.clientHeight, behavior: 'smooth'});
    }
  };

  const renderInfoBox = (onInfoBoxRequestClose: Handler) => (
    <CrmSyncInfoBox
      isAdmin={isAdmin}
      crmSyncSource={CrmSyncSourcesEnum.SALESFORCE}
      onRequestClose={onInfoBoxRequestClose}
      title={
        <Trans id="accounts-crm-salesforce-dialog-infobox-title">1-way sync from Salesforce to Front</Trans>
      }
    />
  );

  const maybeRenderCrmSyncSteps = (hasCrmSync: boolean) => {
    if (isShowingErrorsPage) {
      return null;
    }
    return (
      <>
        <CrmSyncStepOneContainer
          crmLogoImgSrc={SalesforceImgSrc}
          hasExpiredCredentials={hasExpiredCredentials}
          isConnected={isConnected}
          crmSyncSource={CrmSyncSourcesEnum.SALESFORCE}
          username={username}
        />
        <SalesforceCrmSyncStepTwo
          isAdmin={isAdmin}
          isDisabled={Boolean(activeSyncJob) || hasExpiredCredentials || !hasCrmSync}
          selectedAccountType={selectedAccountType}
          salesforceMetadata={salesforceMetadata}
          onSelectAccountType={onSelectAccountType}
          isLoading={isSectionsLoading}
          isConnected={Boolean(isConnectedAndValid && crmSync)}
          onHasError={(hasError) => setHasValidList(!hasError)}
        />
        <SalesforceCrmSyncStepThree
          panelId={panelId}
          crmSyncId={crmSync?.id}
          isAdmin={isAdmin}
          hidePreview={selectedAccountType?.totalAccounts === 0}
          isDisabled={computeIsStepThreeDisabled(
            hasCrmSync,
            selectedAccountType,
            isConnectedAndValid,
            crmSync,
            activeSyncJob,
            hasExpiredCredentials,
            salesforceMetadata,
          )}
          mappedFields={mappedFields}
          salesforceFields={salesforceFields}
          salesforceMetadata={salesforceMetadata}
          onRemoveMappedField={onRemoveMappedField}
          onAddMappedField={onAddMappedField}
          onUpdateMappedField={onUpdateMappedField}
          isLoading={isSectionsLoading}
          externalListId={selectedAccountType?.listId}
        />
      </>
    );
  };

  return (
    <CrmSyncDialogContainer
      status={crmSync?.status}
      contentWrapperRef={contentWrapperRef}
      numAccounts={selectedAccountType?.totalAccounts}
      title={renderSalesforceDialogTitle(isShowingErrorsPage)}
      source={CrmSyncSourcesEnum.SALESFORCE}
      onSyncClick={onUpdateCrmSyncAsync}
      onDisableSyncClick={() => {
        if (!crmSync) {
          return;
        }
        disableCrmSyncAsync(crmSync.id);
      }}
      onRequestClose={onRequestClose}
      onBackClick={isShowingErrorsPage ? () => setIsShowingErrorsPage(false) : undefined}
      isSyncDisabled={computeIsSyncDisabled(
        isConnectedAndValid,
        isAdmin,
        selectedAccountType,
        salesforceMetadata,
        isUpdatingCrmSync,
        hasValidList,
        activeSyncJob,
        isSectionsLoading,
        hasInvalidMappedField,
      )}
      renderInfoBox={isShowingErrorsPage ? undefined : renderInfoBox}
      renderBanners={() =>
        renderBanners(crmSync, activeSyncJob, isShowingErrorsPage, () => setIsShowingErrorsPage(true))
      }
      hasCrmSyncFeature={hasCrmSyncFeature}
    >
      {maybeRenderErrorsPage(
        isShowingErrorsPage,
        () => setIsShowingErrorsPage(false),
        crmSync?.externalUrl,
        crmSync?.errors,
      )}
      {maybeRenderCrmSyncSteps(hasCrmSyncFeature)}
    </CrmSyncDialogContainer>
  );
};

/*
 * Helpers.
 */

function maybeRenderErrorsPage(
  isShowingErrorsPage: boolean,
  onBackToMainPage: Handler,

  externalUrl?: string,
  errors?: ReadonlyArray<CrmSyncErrorModel>,
) {
  if (!isShowingErrorsPage) {
    return null;
  }
  return (
    <CrmSyncErrorsPageContainer
      source={CrmSyncSourcesEnum.SALESFORCE}
      errors={errors ?? []}
      externalUrl={externalUrl}
      onBackToMainPage={onBackToMainPage}
    />
  );
}

function renderBanners(
  crmSync: CrmSyncModel | undefined,
  activeSyncJob: SyncJobModel | undefined,
  isShowingErrorsPage: boolean,
  onViewErrorsClick: Handler,
) {
  if (isShowingErrorsPage || crmSync?.status === CrmSyncStatusesEnum.DISABLED) {
    return null;
  }
  if (activeSyncJob) {
    return <CrmSyncingBanner job={activeSyncJob} />;
  }
  return (
    <>
      <CrmLastSyncedBannerContainer
        source={CrmSyncSourcesEnum.SALESFORCE}
        resourceType={CrmSyncIntegrationResourceTypesEnum.ACCOUNT}
        shouldBoldDates
      />
      {crmSync && <CrmSyncErrorsBannerContainer syncId={crmSync.id} onViewErrorsClick={onViewErrorsClick} />}
    </>
  );
}

function renderSalesforceDialogTitle(isShowingErrorsPage?: boolean) {
  if (isShowingErrorsPage) {
    return <Trans id="accounts-crm-salesforce-dialog-errors-title">Syncing errors</Trans>;
  }
  return <Trans id="accounts-crm-salesforce-dialog-title">Sync accounts from Salesforce</Trans>;
}

function computeIsStepThreeDisabled(
  hasCrmSyncFeature: boolean,
  selectedAccountType?: SalesforceAccountType,
  isConnectedAndValid?: boolean,
  crmSync?: CrmSyncModel,
  activeSyncJob?: SyncJobModel,
  hasExpiredCredentials?: boolean,
  salesforceMetadata?: CrmSalesforceMetadataModel,
) {
  // If we do not have a value or we have a list value selected but no actual list.
  return (
    !hasCrmSyncFeature ||
    !crmSync ||
    !isConnectedAndValid ||
    !isValidListSelected(selectedAccountType, salesforceMetadata) ||
    Boolean(activeSyncJob) ||
    Boolean(hasExpiredCredentials)
  );
}

/**
 * A select account type is only valid if we have all accounts selected or a list selected.
 */
function isValidListSelected(
  selectedAccountType?: SalesforceAccountType,
  salesforceMetadata?: CrmSalesforceMetadataModel,
) {
  if (!selectedAccountType || !salesforceMetadata) {
    return false;
  }
  if (
    selectedAccountType.type === SalesforceAccountTypesEnum.ALL &&
    !isMaxSalesforceAllAccountsReached(salesforceMetadata)
  ) {
    return true;
  }

  return Boolean(selectedAccountType.type === SalesforceAccountTypesEnum.LIST && selectedAccountType.listId);
}

function computeSelectedAccountType(crmSync?: CrmSyncModel): SalesforceAccountType | undefined {
  if (!crmSync || !crmSync.metadata.externalListId) {
    return undefined;
  }
  if (crmSync.metadata.externalListId === SalesforceAccountTypesEnum.ALL) {
    return {
      type: SalesforceAccountTypesEnum.ALL,
    };
  }
  return {
    type: SalesforceAccountTypesEnum.LIST,
    listId: crmSync.metadata.externalListId,
  };
}

function computeIsSyncDisabled(
  isConnectedAndValid: boolean,
  isAdmin: boolean,
  selectedAccountType?: SalesforceAccountType,
  salesforceMetadata?: CrmSalesforceMetadataModel,
  isUpdatingCrmSync?: boolean,
  hasValidList?: boolean,
  activeSyncJob?: SyncJobModel,
  isSectionsLoading?: boolean,
  hasInvalidMappedField?: boolean,
) {
  return (
    isUpdatingCrmSync ||
    !hasValidList ||
    !isConnectedAndValid ||
    !isAdmin ||
    !isValidListSelected(selectedAccountType, salesforceMetadata) ||
    selectedAccountType?.totalAccounts === 0 ||
    // If we have an active sync job.
    Boolean(activeSyncJob) ||
    isSectionsLoading ||
    hasInvalidMappedField
  );
}
